import { inject, Injectable } from '@angular/core';
import { ConfigBaseFilter, ConfigService, ListLayout, ListLayoutMultipleComponent, ListSortOption } from 'gung-list';
import { GungBase } from '../../models';
import { first, forkJoin, Observable, of, switchMap } from 'rxjs';
import { OpenCartTableComponent } from '../../components/open-cart-table/open-cart-table.component';
import { CartRow } from '../../state/cart/types';
import { CustomerService } from '../customers/customer.service';
import { GungFlowService } from '../gung-flow/gung-flow.service';
import { TranslateService } from '@ngx-translate/core';
import { SelectedCustomerService } from '../selected-customer/selected-customer.service';
import { CartService } from '../cart/cart.service';

export interface OpenCart extends GungBase {
  customerId: string;
  flowId: string;
  rows: CartRow[];
  creationDate: Date,
  currentCart: boolean
}

@Injectable({
  providedIn: 'root'
})
export class OpenCartsListConfigService implements ConfigService<OpenCart> {

  protected customerService = inject(CustomerService);
  protected gungFlowService = inject(GungFlowService);
  protected translateService = inject(TranslateService);
  protected cartService = inject(CartService);
  protected selectedCustomerService = inject(SelectedCustomerService);

  parseFailCount = 0;

  getItems(): Observable<OpenCart[]> {
    this.parseFailCount = 0;
    let openCarts: OpenCart[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (key && key.startsWith('cart_')) {
        const value = localStorage.getItem(key);

        const match = key.match(/^cart_(.+)_(\d+)$/);

        if (match) {
          const customerId = match[1];
          const flowId = match[2];

          let rows: CartRow[] = [];
          try {
            rows = value ? JSON.parse(value) : [];
          } catch {
            this.parseFailCount++;
            continue;
          }
          const oldest = rows.length
            ? new Date(
              Math.min(
                ...rows
                  .filter(r => r.timeAdded)
                  .map(r => new Date(r.timeAdded).getTime())
              )
            )
            : null;

          openCarts.push({
            id: key,
            customerId,
            flowId,
            name: '',
            extra: {},
            rows,
            creationDate: oldest,
            currentCart: false
          });
        }
      }
    }
    return this.cartService.getCurrentCart().pipe(
      switchMap(cart =>
        forkJoin({
          customers: this.customerService.getCustomers().pipe(first()),
          flows: this.gungFlowService.getGungFlows().pipe(first()),
          selectedCustomer: this.selectedCustomerService.getSelectedCustomer().pipe(first()),
          selectedFlow: this.gungFlowService.getSelectedFlow().pipe(first())
        })
      ),
      switchMap(({ customers, flows, selectedCustomer, selectedFlow}) => {
        const customerMap = new Map(customers.map(c => [String(c.id), c]));
        const flowMap = new Map(flows.map(f => [String(f.id), f]));

        const currentCustomerId = selectedCustomer ? String(selectedCustomer.id) : null;
        const currentFlowId = selectedFlow ? String(selectedFlow.id) : null;

        const filtered = openCarts
          .filter(cart =>
            customerMap.has(cart.customerId) &&
            flowMap.has(cart.flowId)
          )
          .filter(cart => cart.rows.length > 0)
          .map(cart => {
            const customer = customerMap.get(cart.customerId);
            const flow = flowMap.get(cart.flowId);
            const isCurrent = currentCustomerId === cart.customerId && currentFlowId === cart.flowId;
            return {
              ...cart,
              name: `${customer.name} - ${flow.name}`,
              currentCart: isCurrent
            };
          });
        filtered.sort((a, b) => (b.creationDate?.getTime() ?? 0) - (a.creationDate?.getTime() ?? 0));
        return of(filtered);
      })
    )
  }

  getFilters(): ConfigBaseFilter<OpenCart>[] {
    return [];
  }

  getSortOptions(): ListSortOption<OpenCart>[] {
    return [];
  }

  getBatchSizes(): number[] {
    return [24];
  }

  getLayouts(): ListLayout<OpenCart>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => OpenCartTableComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'Product grid'
      }
    ];
  }

  getSearchTerms(item: OpenCart): string[] {
    const searchTerms = [];
    const savedCart = item as OpenCart;

    if (item.name) {
      searchTerms.push(item.name);
    }

    if (savedCart.customerId) {
      searchTerms.push(savedCart.customerId);
    }

    return searchTerms;
  }

  getItemId(item: OpenCart): string {
    return item.id;
  }

  getNothingFoundTranslateTag() {
    return 'NOTHING_FOUND';
  }
}
